import React, { ComponentProps, PropsWithChildren } from 'react'

import useMediaQuery from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'

import { Checkbox } from '../ui/checkbox'
import {
  DropdownMenu as DropdownMenuRoot,
  DropdownMenuContent,
  // DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'

import { Button, SelectButton } from './Button'
import { Text } from './Text'

// export { DropdownMenuItem }

type DropdownMenuProps = ComponentProps<typeof DropdownMenuRoot> & {
  trigger: ComponentProps<typeof DropdownMenuTrigger>
  content?: Omit<ComponentProps<typeof DropdownMenuContent>, 'children'>
  secondary?: boolean
}

const DropdownMenu: React.FC<PropsWithChildren<DropdownMenuProps>> = ({
  children,
  trigger,
  content,
  secondary,
  ...rest
}) => {
  const { children: triggerChildren, ...triggerProps } = trigger
  const { className: contentClassName, ...contentProps } = content || {}

  const { gtXL } = useMediaQuery()

  return (
    <DropdownMenuRoot {...rest}>
      <DropdownMenuTrigger {...triggerProps}>
        {triggerChildren}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align={gtXL ? 'end' : 'start'}
        sideOffset={secondary ? 8 : 12}
        className={cn(
          'mx-2 w-[calc(100vw-16px)] rounded-md xs:mx-4 xs:w-[calc(100vw-32px)] md:mx-0 md:w-auto',
          secondary ? 'bg-popover-secondary p-2 xl:p-4' : 'p-4 xl:p-6',
          contentClassName
        )}
        {...contentProps}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  )
}

export default DropdownMenu

// 多选下拉菜单
export const DropdownSelect: React.FC<
  PropsWithChildren<Omit<DropdownMenuProps, 'trigger'>> & {
    options: any[]
    value: any | any[]
    labelKey?: string
    valueKey?: string
    trigger?: ComponentProps<typeof DropdownMenuTrigger>
    triggerClassName?: string,
    onSelect?: (item: any) => void
  }
> = ({
  options,
  value,
  labelKey,
  onSelect,
  trigger,
  triggerClassName,
  ...rest
}) => {
  const [open, setOpen] = React.useState(false)

  const memoMultiple = React.useMemo(() => {
    return Array.isArray(value)
  }, [value])
  
  const memoSelected = React.useMemo(() => {
    return options.filter((item) => {
      const val = labelKey ? item[labelKey] : item
      if (memoMultiple) {
        return value.includes(val)
      } else {
        return value === val
      }
    })
  }, [value, memoMultiple, options, labelKey])

  const memoText = React.useMemo(() => {
    return memoSelected.map((item) => {
      return labelKey ? item[labelKey] : item
    }).join('/')
  }, [memoSelected, labelKey])

  return (
    <DropdownMenu
      open={open}
      secondary
      onOpenChange={setOpen}
      content={{
        align: 'end'
      }}
      trigger={
        trigger ?? {
          asChild: true,
          children: (
            <SelectButton
              className={cn(triggerClassName)}
              open={open}
              variant="secondary"
            >
              <Text className="w-0 flex-1 overflow-hidden overflow-ellipsis text-left text-xs xl:flex-auto xl:text-base">
                {memoText}
              </Text>
            </SelectButton>
          )
        }
      }
      {...rest}
    >
      <div className="grid gap-1">
        {options.map((item: any, index) => (
          <Button
            type="button"
            key={index}
            border={false}
            size="md"
            link
            className={cn('!px-2 py-2 hover:text-primary')}
            onClick={() => onSelect && onSelect(item)}
          >
            <span className="w-0 flex-1 overflow-hidden overflow-ellipsis text-left">
              {labelKey == undefined ? item : item[labelKey]}
            </span>
            <Checkbox
              className="size-4"
              id={item}
              checked={memoSelected.includes(item)}
            />
          </Button>
        ))}
      </div>
    </DropdownMenu>
  )
}

// // 多选下拉菜单
// export const DropdownSelect: React.FC<
//   PropsWithChildren<Omit<DropdownMenuProps, 'trigger'>> & {
//     options: any[]
//     value: any | any[]
//     labelKey?: string
//     valueKey?: string
//     multiple?: boolean
//     trigger?: ComponentProps<typeof DropdownMenuTrigger>
//     onChange?: (value: any, selected: any[]) => void
//   }
// > = ({
//   options,
//   value,
//   valueKey,
//   labelKey,
//   multiple = false,
//   onChange,
//   trigger,
//   ...rest
// }) => {
//   const [open, setOpen] = React.useState(false)
//   const [selected, setSelected] = React.useState<any[]>([])

//   const onSelecet = (item: any) => {
//     if (item === true) {
//       setSelected(options)
//     } else if (multiple) {
//       const index = selected.findIndex((a) => a === item)
//       if (index < 0) {
//         setSelected((prev) => {
//           return [...prev, item]
//         })
//       } else {
//         setSelected((prev) => {
//           return prev.filter((a) => a !== item)
//         })
//       }
//     } else {
//       setSelected([item])
//     }
//   }

//   const initSelected = () => {
//     let initArr = []
//     if (multiple && Array.isArray(value)) {
//       if (value.length === 0) {
//         return
//       }
//       initArr = options.filter((item) => {
//         if (valueKey) {
//           return value.includes(item[valueKey])
//         } else {
//           return value.includes(item)
//         }
//       })
//     } else {
//       const item = options.find((item) => {
//         if (valueKey) {
//           return value === item[valueKey]
//         } else {
//           return value === item
//         }
//       })
//       initArr = [item]
//     }
//     setSelected(initArr)
//   }

//   const memoText = React.useMemo(() => {
//     let textValue
//     const key = labelKey || valueKey
//     if (key) {
//       textValue = selected.map((item) => item[key]).join('/')
//     } else {
//       textValue = selected
//         .filter((item) => {
//           return typeof item === 'string' || typeof item === 'number'
//         })
//         .join('/')
//     }
//     return textValue
//   }, [selected, labelKey, valueKey])

//   React.useEffect(() => {
//     let val
//     if (multiple) {
//       val = selected.map((item) => {
//         if (valueKey) {
//           return item[valueKey]
//         } else {
//           return item
//         }
//       })
//     } else {
//       val = valueKey ? selected[0]?.([valueKey]) : selected[0]
//     }
//     onChange && onChange(val, selected)
//   }, [selected, multiple, valueKey, onChange])

//   React.useEffect(() => {
//     initSelected()
//   }, [])

//   return (
//     <DropdownMenu
//       open={open}
//       secondary
//       onOpenChange={setOpen}
//       content={{
//         align: 'end'
//       }}
//       trigger={
//         trigger ?? {
//           asChild: true,
//           children: (
//             <SelectButton
//               role="token-list-sorter"
//               open={open}
//               variant="secondary"
//             >
//               <Text className="flex-1 text-left text-xs xl:flex-auto xl:text-base">
//                 {memoText}
//               </Text>
//             </SelectButton>
//           )
//         }
//       }
//       {...rest}
//     >
//       <div className="grid gap-1">
//         {/* {multiple && (
//           <Button
//             type="button"
//             border={false}
//             size="md"
//             link
//             className={cn(
//               'justify-start !px-0 py-2 hover:text-primary',
//               false && 'text-primary-foreground'
//             )}
//             onClick={() => onSelecet(true)}
//           >
//             All
//           </Button>
//         )} */}
//         {options.map((item: any, index) => (
//           <Button
//             type="button"
//             key={index}
//             border={false}
//             size="md"
//             link
//             className={cn(
//               'justify-start !px-0 py-2 hover:text-primary',
//               !selected.includes(item) && 'text-primary-foreground'
//             )}
//             onClick={() => onSelecet(item)}
//           >
//             {labelKey == undefined ? item : item[labelKey]}
//           </Button>
//         ))}
//       </div>
//     </DropdownMenu>
//   )
// }
